import { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetLookUpDropdownListQuery, useGetMainCategoriesQuery, useGetSecondCategoriesQuery } from '../../../../api';
import { usePermissions } from '../../../../hooks/usePermissions';
import UserDropdown from '../../../../components/common/UserDropdown';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import './TechnicalSupportRequestTable.scss';

const TEXT_FILTER_FIELDS = [
    {
        field: 'chassisNo',
        matchModeField: 'chassisNoMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'vehicleModelEmissions',
        matchModeField: 'vehicleModelEmissionsMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'modelYear',
        matchModeField: 'modelYearMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'dealerName',
        matchModeField: 'dealerNameMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'userNameSurname',
        matchModeField: 'userNameSurnameMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'errorDescription',
        matchModeField: 'errorDescriptionMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    },
    {
        field: 'technicalResponsibleUserName',
        matchModeField: 'technicalResponsibleUserNameMatchMode',
        defaultMatchMode: FilterMatchMode.CONTAINS
    }
];

const getFilterValue = (filters, field, fallbackValue = null) => (field in filters ? filters[field]?.value ?? null : fallbackValue);

const getFilterMatchMode = (filters, field, fallbackMatchMode = FilterMatchMode.CONTAINS) => (field in filters ? filters[field]?.matchMode ?? fallbackMatchMode : fallbackMatchMode);

const mapLookupOptions = (items, labelSelector, valueSelector) =>
    (items || []).map((item) => ({
        label: labelSelector(item),
        value: valueSelector(item)
    }));

const buildTextTableFilters = (activeFilters) =>
    TEXT_FILTER_FIELDS.reduce((acc, config) => {
        acc[config.field] = {
            value: activeFilters?.[config.field] ?? null,
            matchMode: activeFilters?.[config.matchModeField] ?? config.defaultMatchMode
        };
        return acc;
    }, {});

const buildUpdatedTextFilters = (filters, activeFilters) =>
    TEXT_FILTER_FIELDS.reduce((acc, config) => {
        acc[config.field] = getFilterValue(filters, config.field, activeFilters?.[config.field] ?? null);
        acc[config.matchModeField] = getFilterMatchMode(filters, config.field, activeFilters?.[config.matchModeField] ?? config.defaultMatchMode);
        return acc;
    }, {});

const isSameFilterValue = (left, right) => String(left ?? '') === String(right ?? '');

const TechnicalSupportRequestTable = ({ requests, loading, totalCount, lazyParams, onPage, activeFilters, onFiltersChange, onResetFilters, sortField, sortOrder, onSortChange, resetCounter, onEdit }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasAdminRole, hasWarrantyTeamRole } = usePermissions();
    const canSeeManagerFilters = hasAdminRole() || hasWarrantyTeamRole();

    const { data: statusOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: getCurrentLanguageId(),
        GroupCode: 'TECHNICAL_CURRENT_STATUS'
    });
    const { data: errorEffectLookupOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: getCurrentLanguageId(),
        GroupCode: 'ERROR_EFFECT'
    });
    const { data: countryLookupOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: getCurrentLanguageId(),
        GroupCode: 'COUNTRIES'
    });

    const { data: mainCategoryResponse } = useGetMainCategoriesQuery({
        LanguageId: getCurrentLanguageId(),
        PageNumber: 1,
        PageSize: 200
    });

    const selectedMainSystem = activeFilters?.mainSystemLookupCode;
    const { data: secondaryCategoryResponse } = useGetSecondCategoriesQuery(
        {
            LanguageId: getCurrentLanguageId(),
            mainCategory: selectedMainSystem,
            PageNumber: 1,
            PageSize: 200
        },
        { skip: !selectedMainSystem }
    );

    const errorEffectOptions = useMemo(
        () =>
            mapLookupOptions(
                errorEffectLookupOptions,
                (item) => item?.translatedText || item?.text || String(item?.code || item?.lookupCode),
                (item) => {
                    const rawValue = item?.code ?? item?.lookupCode;
                    const numericValue = Number(rawValue);
                    return Number.isFinite(numericValue) ? numericValue : null;
                }
            ).filter((item) => item.value !== null),
        [errorEffectLookupOptions]
    );

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const openClosedOptions = useMemo(
        () => [
            { label: th('technicalSupport.columnHeaders.open'), value: '1' },
            { label: th('technicalSupport.columnHeaders.closed'), value: '0' }
        ],
        [th]
    );

    const statusFilterOptions = useMemo(
        () =>
            mapLookupOptions(
                statusOptions,
                (item) => item?.translatedText || item?.text || String(item?.code || item?.lookupCode),
                (item) => item?.code ?? item?.lookupCode ?? null
            ),
        [statusOptions]
    );

    const countryOptions = useMemo(
        () =>
            countryLookupOptions.map((item) => ({
                label: item.translatedText,
                value: item.translatedText
            })),
        [countryLookupOptions]
    );

    const mainSystemOptions = useMemo(
        () =>
            mapLookupOptions(
                mainCategoryResponse?.data,
                (item) => item?.mainCategoryName || item?.text || item?.translatedText || String(item?.lookupCode),
                (item) => item?.lookupCode
            ),
        [mainCategoryResponse]
    );

    const subSystemOptions = useMemo(
        () =>
            mapLookupOptions(
                secondaryCategoryResponse?.data,
                (item) => item?.secondaryCategoryName || item?.text || item?.translatedText || String(item?.lookupCode),
                (item) => item?.lookupCode
            ),
        [secondaryCategoryResponse]
    );

    const localeByLangId = (langId) => {
        switch (langId) {
            case 1:
                return 'tr-TR';
            case 2:
                return 'en-US';
            case 3:
                return 'de-DE';
            default:
                return 'tr-TR';
        }
    };

    const dateBodyTemplate = (rowData, field) => {
        const dateValue = rowData?.[field];
        if (!dateValue) return '-';

        const locale = localeByLangId(getCurrentLanguageId());
        const dt = new Date(dateValue);

        if (Number.isNaN(dt.getTime())) return '-';

        return new Intl.DateTimeFormat(locale).format(dt);
    };

    const getTagStyle = (code) => {
        const numCode = Number(code);
        if ([5, 6, 7, 8].includes(numCode)) {
            return { backgroundColor: '#FFFF00', color: '#000000', border: 'none' };
        }
        return undefined;
    };

    const statusBodyTemplate = (rowData) => {
        const statusCode = rowData?.statusNotification;

        if (statusCode === null || statusCode === undefined || statusCode === '') {
            return <span className="text-500">-</span>;
        }

        const statusItem = statusOptions.find((item) => String(item.code || item.lookupCode) === String(statusCode));

        const statusText = statusItem?.translatedText || statusItem?.text || th('common.unknown');

        const numCode = Number(statusCode);
        let severity;
        if ([1, 2, 3, 4].includes(numCode)) severity = 'warning';
        else if (numCode === 9) severity = 'success';
        else severity = null;

        return <Tag value={statusText} severity={severity} style={getTagStyle(statusCode)} />;
    };

    const openClosedBodyTemplate = (rowData) => {
        const value = rowData?.statusNotificationInf;

        if (value === null || value === undefined || value === '') {
            return <span className="text-500">-</span>;
        }

        const option = openClosedOptions.find((item) => item.value === String(value));

        return <span>{option?.label || th('common.unknown')}</span>;
    };

    const errorEffectBodyTemplate = (rowData) => {
        const effectValue = rowData?.errorEffect;

        if (effectValue === null || effectValue === undefined || effectValue === '') {
            return <span className="text-500">-</span>;
        }

        const effectOption = errorEffectOptions.find((opt) => opt.value === effectValue);
        const effectText = effectOption?.label || th('common.unknown');

        return <span>{effectText}</span>;
    };

    const solutionDurationBodyTemplate = (rowData) => {
        if (rowData?.gunFarki === null || rowData?.gunFarki === undefined || rowData?.gunFarki === '') {
            return <span className="text-500">-</span>;
        }

        const duration = Number(rowData.gunFarki);

        if (!Number.isFinite(duration)) {
            return <span className="text-500">-</span>;
        }

        const style = duration > 7 ? { color: '#d32f2f', fontWeight: 'bold' } : undefined;

        return (
            <span style={style}>
                {duration > 7 && <span style={{ marginRight: '4px' }}>🔴</span>}
                {duration}
            </span>
        );
    };

    const renderUserList = (users) => {
        if (!Array.isArray(users) || users.length === 0) {
            return <span className="text-500">-</span>;
        }

        const userNames = users
            .map((user) => user.userName)
            .filter(Boolean)
            .join(', ');
        return (
            <div className="text-overflow-ellipsis overflow-hidden white-space-nowrap" title={userNames}>
                {userNames}
            </div>
        );
    };

    const createTextColumn = (field, header, options = {}) => {
        const { body, className, sortable = true } = options;
        return <Column field={field} header={header} body={body} sortable={sortable} filter filterPlaceholder={th('common.search')} filterMatchModeOptions={textMatchModeOptions} showClearButton={false} className={className} />;
    };

    const tableFilters = useMemo(
        () => ({
            ...buildTextTableFilters(activeFilters),
            createdDate: {
                value: activeFilters?.createdDate ?? null,
                matchMode: activeFilters?.createdDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            solutionDate: {
                value: activeFilters?.solutionDate ?? null,
                matchMode: activeFilters?.solutionDateMatchMode ?? FilterMatchMode.DATE_IS
            },
            errorEffect: {
                value: activeFilters?.errorEffect ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            statusNotificationInf: {
                value: activeFilters?.statusNotificationInf ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            country: {
                value: activeFilters?.country ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            statusNotification: {
                value: activeFilters?.statusNotification ?? [],
                matchMode: FilterMatchMode.IN
            },
            mainSystemLookupCode: {
                value: activeFilters?.mainSystemLookupCode ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            altSystemLookupCode: {
                value: activeFilters?.altSystemLookupCode ?? null,
                matchMode: FilterMatchMode.EQUALS
            },
            technicalSupportRegionalManager: {
                value: activeFilters?.regionalManagerUserIds ?? [],
                matchMode: FilterMatchMode.IN
            },
            technicalSupportResponsible: {
                value: activeFilters?.technicalResponsibleUserIds ?? [],
                matchMode: FilterMatchMode.IN
            },
            isVor: {
                value: activeFilters?.isVor ?? null,
                matchMode: FilterMatchMode.EQUALS
            }
        }),
        [activeFilters]
    );

    const extractUserIds = (value) => {
        if (!Array.isArray(value)) return [];
        return value
            .map((item) => {
                if (typeof item === 'object' && item !== null) return String(item.id ?? '');
                return String(item ?? '');
            })
            .filter((item) => item !== '');
    };

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const currentMainSystem = activeFilters?.mainSystemLookupCode ?? null;
        const nextMainSystem = getFilterValue(filters, 'mainSystemLookupCode', currentMainSystem);
        const mainSystemChanged = !isSameFilterValue(nextMainSystem, currentMainSystem);
        const updatedTextFilters = buildUpdatedTextFilters(filters, activeFilters);

        const updatedFilters = {
            ...(activeFilters || {}),
            ...updatedTextFilters,
            createdDate: getFilterValue(filters, 'createdDate', activeFilters?.createdDate ?? null),
            createdDateMatchMode: getFilterMatchMode(filters, 'createdDate', FilterMatchMode.DATE_IS),
            solutionDate: getFilterValue(filters, 'solutionDate', activeFilters?.solutionDate ?? null),
            solutionDateMatchMode: getFilterMatchMode(filters, 'solutionDate', FilterMatchMode.DATE_IS),
            errorEffect: getFilterValue(filters, 'errorEffect', activeFilters?.errorEffect ?? null),
            statusNotificationInf: getFilterValue(filters, 'statusNotificationInf', activeFilters?.statusNotificationInf ?? null),
            country: getFilterValue(filters, 'country', activeFilters?.country ?? null),
            countryMatchMode: FilterMatchMode.EQUALS,
            statusNotification: getFilterValue(filters, 'statusNotification', activeFilters?.statusNotification ?? []),
            mainSystemLookupCode: nextMainSystem,
            altSystemLookupCode: mainSystemChanged ? null : getFilterValue(filters, 'altSystemLookupCode', activeFilters?.altSystemLookupCode ?? null),
            regionalManagerUserIds: 'technicalSupportRegionalManager' in filters ? extractUserIds(filters.technicalSupportRegionalManager?.value) : activeFilters?.regionalManagerUserIds ?? [],
            technicalResponsibleUserIds: 'technicalSupportResponsible' in filters ? extractUserIds(filters.technicalSupportResponsible?.value) : activeFilters?.technicalResponsibleUserIds ?? [],
            isVor: getFilterValue(filters, 'isVor', activeFilters?.isVor ?? null)
        };

        onFiltersChange?.(updatedFilters);
    };

    const getLookupItemTextByGroupCode = (lookupItems, groupCode) => {
        if (!Array.isArray(lookupItems)) return '-';
        const item = lookupItems.find((lookupItem) => lookupItem?.groupCode === groupCode);
        if (!item) return '-';
        return item?.text || item?.translatedText || item?.code || '-';
    };

    const mainSystemBodyTemplate = (rowData) => getLookupItemTextByGroupCode(rowData?.lookupItems, 'XXBOS_MAIN_CATEGORY');

    const subSystemBodyTemplate = (rowData) => getLookupItemTextByGroupCode(rowData?.lookupItems, 'XXBOS_SECONDARY_CATEGORY');

    const actionBodyTemplate = (rowData) => <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem', color: '#0B3187' }} onClick={() => onEdit(rowData)} title={th('common.edit')} />;

    const handlePage = (e) => {
        onPage?.(e);
    };

    const handleSort = (e) => {
        onSortChange?.(e);
    };

    const actionFilterTemplate = () => (
        <div className="flex justify-content-center align-items-center" style={{ width: '100%' }}>
            <i onClick={onResetFilters} className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" style={{ color: '#0B3187', fontSize: '1rem' }} title={th('filter.reset')} />
        </div>
    );

    const statusFilterTemplate = (options) => (
        <MultiSelect
            value={options.value}
            options={statusFilterOptions}
            onChange={(e) => options.filterApplyCallback(e.value ?? [])}
            placeholder={th('common.search')}
            className="p-column-filter"
            maxSelectedLabels={1}
            selectedItemsLabel={th('common.selectedCount', { count: options.value?.length || 0 })}
            filter
            filterPlaceholder={th('common.search')}
        />
    );

    const errorEffectFilterTemplate = (options) => <Dropdown value={options.value} options={errorEffectOptions} onChange={(e) => options.filterApplyCallback(e.value ?? null)} placeholder={th('common.search')} className="p-column-filter" showClear />;

    const openClosedFilterTemplate = (options) => (
        <Dropdown value={options.value} options={openClosedOptions} onChange={(e) => options.filterApplyCallback(e.value ?? null)} placeholder={th('common.search')} className="p-column-filter" showClear />
    );

    const countryFilterTemplate = (options) => <Dropdown value={options.value} options={countryOptions} onChange={(e) => options.filterApplyCallback(e.value ?? null)} placeholder={th('common.search')} className="p-column-filter" showClear filter />;

    const mainSystemFilterTemplate = (options) => (
        <Dropdown value={options.value} options={mainSystemOptions} onChange={(e) => options.filterApplyCallback(e.value ?? null)} placeholder={th('common.search')} className="p-column-filter" showClear filter />
    );

    const subSystemFilterTemplate = (options) => (
        <Dropdown value={options.value} options={subSystemOptions} onChange={(e) => options.filterApplyCallback(e.value ?? null)} placeholder={th('common.search')} className="p-column-filter" showClear filter disabled={!selectedMainSystem} />
    );

    const dateFilterTemplate = (options) => (
        <Calendar
            value={options.value}
            onChange={(e) => {
                let normalizedDate = e.value;
                if (normalizedDate instanceof Date) {
                    normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
                }
                options.filterApplyCallback(normalizedDate ?? null);
            }}
            dateFormat="dd.mm.yy"
            showIcon
            showButtonBar
            className="p-column-filter"
            placeholder={th('common.search')}
        />
    );

    const regionalManagerFilterTemplate = (options) => {
        // Convert single value to array format for display
        const currentValue = options.value && options.value.length > 0 ? [options.value[0]] : [];

        return (
            <UserDropdown
                value={currentValue}
                onChange={(selectedUsers) => {
                    // Only take the last selected user (most recent selection)
                    const lastUser = selectedUsers && selectedUsers.length > 0 ? selectedUsers[selectedUsers.length - 1] : null;
                    options.filterApplyCallback(lastUser ? extractUserIds([lastUser]) : []);
                }}
                placeholder={th('common.search')}
                className="p-column-filter"
            />
        );
    };

    const createUserColumn = (field, headerKey, filterTemplate) => (
        <Column
            field={field}
            header={th(headerKey)}
            filter={canSeeManagerFilters}
            filterElement={canSeeManagerFilters ? filterTemplate : undefined}
            showFilterMenu={false}
            body={(rowData) => renderUserList(rowData?.[field] || [])}
            className="column-user-list"
        />
    );

    const isVorBodyTemplate = (rowData) => {
        const isVorValue = rowData?.isVor;
        return (
            <div className="flex justify-content-center">
                <Checkbox checked={isVorValue === 1} disabled />
            </div>
        );
    };

    const isVorFilterTemplate = (options) => (
        <div className="flex justify-content-center">
            <Checkbox
                checked={options.value === 1}
                onChange={(e) => {
                    const newValue = e.checked ? 1 : null;
                    options.filterApplyCallback(newValue);
                }}
            />
        </div>
    );

    return (
        <>
            <StickyScrollTable deps={[requests, resetCounter]}>
                <DataTable
                    key={`technical-support-table-${resetCounter || 0}`}
                    value={requests}
                    dataKey="id"
                    lazy
                    scrollable
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows || 10}
                    totalRecords={totalCount}
                    onPage={handlePage}
                    loading={loading}
                    showGridlines
                    stripedRows
                    filterDisplay="row"
                    filters={tableFilters}
                    onFilter={handleTableFilter}
                    emptyMessage={th('technicalSupport.emptyMessage')}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{first} - {last} / {totalRecords}"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    sortMode="single"
                    pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
                    className="technical-support-table"
                >
                    <Column body={actionBodyTemplate} exportable={false} filter filterElement={actionFilterTemplate} showFilterMenu={false} frozen alignFrozen="left" className="column-action" />
                    <Column
                        field="statusNotificationInf"
                        header={th('technicalSupport.columnHeaders.openClosed')}
                        body={openClosedBodyTemplate}
                        sortable={false}
                        filter
                        filterElement={openClosedFilterTemplate}
                        showFilterMenu={false}
                        frozen
                        alignFrozen="left"
                        className="column-open-closed"
                    />
                    <Column
                        field="statusNotification"
                        header={th('technicalSupport.columnHeaders.status')}
                        body={statusBodyTemplate}
                        sortable
                        filter
                        filterElement={statusFilterTemplate}
                        showFilterMenu={false}
                        frozen
                        alignFrozen="left"
                        className="column-status"
                    />
                    <Column field="id" header={th('technicalSupport.columnHeaders.requestNo')} sortable frozen alignFrozen="left" className="column-request-no" />
                    <Column
                        field="chassisNo"
                        header={th('technicalSupport.columnHeaders.chassisNo')}
                        sortable
                        filter
                        filterPlaceholder={th('common.search')}
                        filterMatchModeOptions={textMatchModeOptions}
                        showClearButton={false}
                        frozen
                        alignFrozen="left"
                        className="column-chassis-no"
                    />
                    <Column field="gunFarki" header={th('technicalSupport.columnHeaders.solutionDuration')} body={solutionDurationBodyTemplate} sortable frozen alignFrozen="left" className="column-solution-duration" />
                    <Column
                        field="solutionDate"
                        header={th('technicalSupport.fields.solutionDate')}
                        body={(rowData) => (rowData?.solutionDate ? dateBodyTemplate(rowData, 'solutionDate') : '')}
                        sortable
                        filter
                        filterElement={dateFilterTemplate}
                        showFilterMenu={false}
                        className="column-notification-date"
                    />
                    <Column
                        field="createdDate"
                        header={th('technicalSupport.columnHeaders.requestCreatedDate')}
                        body={(rowData) => dateBodyTemplate(rowData, 'createdDate')}
                        sortable
                        filter
                        filterElement={dateFilterTemplate}
                        showFilterMenu={false}
                        className="column-notification-date"
                    />
                    {createTextColumn('vehicleModelEmissions', th('technicalSupport.fields.vehicleModelEmissions'), {
                        body: (rowData) => createTruncatedTextForTable(rowData?.vehicleModelEmissions, 30, `vehicleModel-${rowData?.id}`),
                        className: 'column-vehicle-model'
                    })}
                    {createTextColumn('modelYear', th('technicalSupport.fields.modelYear'))}
                    {createTextColumn('dealerName', th('technicalSupport.columnHeaders.dealerName'), {
                        className: 'column-dealer-name'
                    })}
                    <Column field="country" header={th('technicalSupport.columnHeaders.country')} sortable={false} filter filterElement={countryFilterTemplate} showFilterMenu={false} className="column-country" />
                    {createTextColumn('userNameSurname', th('technicalSupport.columnHeaders.createdBy'), {
                        body: (rowData) => createTruncatedTextForTable(rowData?.userNameSurname || rowData?.userName || '-', 30, `createdBy-${rowData?.id}`),
                        className: 'column-created-by'
                    })}
                    {createTextColumn('errorDescription', th('technicalSupport.columnHeaders.errorDescription'), {
                        body: (rowData) => createTruncatedTextForTable(rowData?.errorDescription, 40, `errorDesc-${rowData?.id}`),
                        className: 'column-error-description'
                    })}
                    <Column
                        field="errorEffect"
                        header={th('technicalSupport.columnHeaders.errorEffect')}
                        body={errorEffectBodyTemplate}
                        sortable
                        filter
                        filterElement={errorEffectFilterTemplate}
                        showFilterMenu={false}
                        className="column-error-effect"
                    />
                    <Column field="isVor" header={th('technicalSupport.columnHeaders.isVor')} body={isVorBodyTemplate} sortable filter filterElement={isVorFilterTemplate} showFilterMenu={false} className="column-is-vor" />
                    <Column field="mainSystemLookupCode" header={th('technicalSupport.fields.mainSystem')} body={mainSystemBodyTemplate} sortable filter filterElement={mainSystemFilterTemplate} showFilterMenu={false} className="column-main-system" />
                    <Column field="altSystemLookupCode" header={th('technicalSupport.fields.subSystem')} body={subSystemBodyTemplate} sortable filter filterElement={subSystemFilterTemplate} showFilterMenu={false} className="column-sub-system" />
                    {createUserColumn('technicalSupportRegionalManager', 'technicalSupport.columnHeaders.regionalManager', regionalManagerFilterTemplate)}
                    {createTextColumn('technicalResponsibleUserName', th('technicalSupport.responsibilityOrder.primary'), {
                        body: (rowData) => rowData?.technicalResponsibleUserName || <span className="text-500">-</span>,
                        className: 'column-user-list'
                    })}
                </DataTable>
            </StickyScrollTable>
        </>
    );
};

export default TechnicalSupportRequestTable;
